import React, { useState } from 'react';

export default function AuditViewContainer({ logs }) {
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const actionCategories = {
    all: 'All Events',
    records: 'Record Events',
    permissions: 'Permission Events',
    session: 'Session Events'
  };

  const filteredLogs = logs.filter(log => {
    const matchesCategory = filter === 'all'
      || (filter === 'records' && log.action.startsWith('RECORD_'))
      || (filter === 'permissions' && log.action.startsWith('PERMISSION_'))
      || (filter === 'session' && (log.action.startsWith('SESSION_') || log.action.startsWith('AUTH_')));

    const matchesSearch = searchQuery === ''
      || log.action.toLowerCase().includes(searchQuery.toLowerCase())
      || JSON.stringify(log.details).toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  const getActionIcon = (action) => {
    const icons = {
      RECORD_CREATED: '📄',
      RECORD_VIEWED: '👁️',
      RECORD_DELETED: '🗑️',
      RECORD_DOWNLOADED: '⬇️',
      PERMISSION_GRANTED: '✅',
      PERMISSION_REVOKED: '🚫',
      AUTH_LOGIN: '🔐',
      AUTH_LOGOUT: '🔓'
    };
    return icons[action] || '📋';
  };

  const getActionColor = (action) => {
    if (action.includes('DELETED') || action.includes('REVOKED') || action.includes('FAILED')) return 'text-red-600 bg-red-50';
    if (action.includes('CREATED') || action.includes('GRANTED') || action.includes('LOGIN')) return 'text-green-600 bg-green-50';
    if (action.includes('VIEWED')) return 'text-blue-600 bg-blue-50';
    return 'text-slate-600 bg-slate-50';
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Audit Log</h2>
        <p className="text-slate-600 mt-1">Complete history of all actions on your medical records</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <input
            type="text"
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 min-w-[200px] px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex gap-2">
            {Object.entries(actionCategories).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === key
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="max-h-[600px] overflow-y-auto">
          {filteredLogs.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-slate-500">No audit logs found</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Timestamp</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Action</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                        <span>{getActionIcon(log.action)}</span>
                        {log.action.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {log.details?.title || log.details?.recordTitle || log.details?.grantedTo || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-900">
          <strong>HIPAA Compliance Note:</strong> This audit log tracks all access and modifications to protected health information (PHI).
          Logs are retained for 6 years in accordance with HIPAA requirements.
        </p>
      </div>
    </div>
  );
}
